import api from "@/shared/utils/api";
import { settings } from "@/shared/utils/settings";

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
  organization: {
    id: string;
    name: string;
  } | null;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  role: string;
  organization_id: string;
}

export interface UpdateUserPayload {
  user_id: string;
  name?: string;
  email?: string;
  role_id: string | null;
  organization_id?: string | null;
}

export class UserService {
  /**
   * Get all users
   * @returns Promise with all users
   */
  static async getAll(): Promise<User[]> {
    try {
      const response = await api.get(settings.api.admin.users);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch users:", error);
      throw error;
    }
  }

  /**
   * Create a new user
   * @param userData - User data to create
   * @returns Promise with the created user
   */
  static async create(userData: CreateUserPayload): Promise<any> {
    try {
      const response = await api.post(settings.api.admin.users, userData);
      return response.data;
    } catch (error) {
      console.error("Failed to create user:", error);
      throw error;
    }
  }

  /**
   * Delete a user
   * @param id - User ID to delete
   */
  static async delete(id: string): Promise<any> {
    try {
      const response = await api.delete(`${settings.api.admin.users}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Activate a user
   * @param id - User ID to activate
   */
  static async activate(id: string): Promise<any> {
    try {
      const response = await api.post(
        `${settings.api.admin.users}/${id}/activate`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to activate user with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update a user
   * @param updateData - User data to update
   * @returns Promise with the updated user
   */
  static async update(updateData: UpdateUserPayload): Promise<any> {
    try {
      const { organization_id, ...data } = updateData;
      // The organization_id is used in the URL path, not in the request body
      const org_id = organization_id || "";
      const response = await api.put(
        `${settings.api.admin.organizations}/${org_id}/users`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(
        `Failed to update user with ID ${updateData.user_id}:`,
        error
      );
      throw error;
    }
  }
}
